/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

import * as React from "react";

import { cn } from "@/lib/utils";
import { cva, type VariantProps } from "class-variance-authority";

const textareaVariants = cva(
  "flex w-full rounded-[var(--radius-button)] border bg-surface-input text-text-primary placeholder:text-text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--border-focus)] focus-visible:ring-offset-0 disabled:cursor-not-allowed disabled:bg-surface-secondary-nav disabled:opacity-60 transition-[color,background-color,border-color,box-shadow]",
  {
    variants: {
      textareaSize: {
        sm: "min-h-[60px] text-xs px-2 py-1.5",
        default: "min-h-[72px] text-sm px-2.5 py-1.5",
        lg: "min-h-[100px] text-sm px-3 py-2",
      },
    },
    defaultVariants: {
      textareaSize: "default",
    },
  },
);

export interface TextareaProps
  extends
    React.TextareaHTMLAttributes<HTMLTextAreaElement>,
    VariantProps<typeof textareaVariants> {
  /** Show error styling for validation states */
  error?: boolean;
}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, error, textareaSize, ...props }, ref) => {
    return (
      <textarea
        ref={ref}
        aria-invalid={error ? "true" : undefined}
        className={cn(
          textareaVariants({ textareaSize }),
          error
            ? "border-[var(--status-danger)] focus-visible:border-[var(--status-danger)] focus-visible:ring-[var(--status-danger)]"
            : "border-[var(--border-hairline)] focus-visible:border-[var(--border-focus)]",
          className,
        )}
        {...props}
      />
    );
  },
);
Textarea.displayName = "Textarea";

export { Textarea, textareaVariants };
